import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Check, FileText, X } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { useReveal } from "@/hooks/useReveal";
import { getServiceBySlug, SERVICES } from "@/lib/services";
import { SERVICE_FAQS } from "@/lib/serviceFaqs";
import { CALENDLY_URL } from "@/lib/constants";
import { getEntry } from "@/lib/glossary";
import { getPost } from "@/lib/blog";
import ServiceFAQ from "@/components/site/ServiceFAQ";
import InsuranceDisclaimer from "@/components/site/InsuranceDisclaimer";
import NotFound from "./NotFound";

export default function LeistungDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? getServiceBySlug(slug) : undefined;
  useReveal(slug);

  if (!service) return <NotFound />;
  const Icon = service.icon;
  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);
  const faqs = SERVICE_FAQS[service.slug] ?? [];

  const glossaryItems = (service.glossaryLinks ?? [])
    .map((slug) => {
      const e = getEntry(slug);
      return e ? { slug: e.slug, label: e.term } : null;
    })
    .filter((x): x is { slug: string; label: string } => Boolean(x));
  const blogItems = (service.blogLinks ?? [])
    .map((slug) => {
      const p = getPost(slug);
      return p ? { slug: p.slug, label: p.title } : null;
    })
    .filter((x): x is { slug: string; label: string } => Boolean(x));
  const hasInternalLinks = glossaryItems.length > 0 || blogItems.length > 0;

  const canonical = `https://finable.de/leistungen/${service.slug}`;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    alternateName: service.shortName,
    serviceType: service.category,
    description: service.intro,
    url: canonical,
    areaServed: { "@type": "Country", name: "Deutschland" },
    provider: {
      "@type": "FinancialService",
      name: "FINABLE",
      url: "https://finable.de",
      logo: "https://finable.de/logo.png",
    },
    audience: { "@type": "Audience", audienceType: service.forWhom },
  };
  // FAQPage schema is emitted by <ServiceFAQ> — don't duplicate it here.

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://finable.de/" },
      { "@type": "ListItem", position: 2, name: "Leistungen", item: "https://finable.de/leistungen" },
      { "@type": "ListItem", position: 3, name: service.shortName, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={service.metaTitle ?? `${service.shortName} – ${service.tagline} | FINABLE`}
        description={service.metaDescription ?? service.intro.slice(0, 155)}
        canonical={canonical}
        jsonLd={[serviceSchema, breadcrumbSchema]}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative max-w-4xl">
            <Link
              to="/leistungen"
              className="reveal inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Alle Leistungen
            </Link>
            <div className="reveal mt-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-accent/20 backdrop-blur-sm border border-accent/30 flex items-center justify-center">
                <Icon className="w-7 h-7 text-accent" />
              </div>
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">
                {service.category}
              </span>
            </div>
            <h1 className="reveal mt-4 font-display text-4xl md:text-6xl font-extrabold text-balance leading-[1.05]">
              {service.name}
            </h1>
            <p className="reveal mt-5 text-xl md:text-2xl text-accent/90 font-medium">{service.tagline}</p>
            <p className="reveal mt-6 text-lg text-white/80 leading-relaxed max-w-3xl">{service.intro}</p>

            <div className="reveal mt-8 flex flex-wrap gap-3">
              <Button asChild variant="accent" size="lg">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Kostenloses Beratungsgespräch <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Highlights + Für wen */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container max-w-5xl grid md:grid-cols-2 gap-8">
            <div className="reveal rounded-2xl bg-card border border-border p-8 shadow-card">
              <h2 className="font-display text-2xl font-bold text-primary">Für wen lohnt sich das?</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{service.forWhom}</p>
            </div>
            <div className="reveal rounded-2xl bg-gradient-accent text-accent-foreground p-8 shadow-elegant">
              <h2 className="font-display text-2xl font-bold">Auf einen Blick</h2>
              <ul className="mt-4 space-y-3">
                {service.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Sections */}
        {service.sections.map((section, i) => (
          <section
            key={section.heading}
            className={`py-16 md:py-20 ${i % 2 === 0 ? "bg-secondary/40" : "bg-background"}`}
          >
            <div className="container max-w-4xl">
              <div className="reveal">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-primary">{section.heading}</h2>
                {section.intro && (
                  <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{section.intro}</p>
                )}
              </div>

              {section.paragraphs?.map((p, idx) => (
                <p key={idx} className="reveal mt-6 text-muted-foreground leading-relaxed">
                  {p}
                </p>
              ))}

              {section.bullets && (
                <div className="mt-10 grid sm:grid-cols-2 gap-5">
                  {section.bullets.map((b) => (
                    <div
                      key={b.title}
                      className="reveal rounded-2xl bg-card border border-border p-6 shadow-card hover:shadow-elegant transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0">
                          <Check className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="font-display text-lg font-bold text-primary">{b.title}</h3>
                          <p className="mt-1.5 text-muted-foreground leading-relaxed">{b.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}

        {/* Was nicht versichert ist */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container max-w-4xl">
            <div className="reveal rounded-2xl bg-card border border-border p-8 md:p-10 shadow-card">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary">Was ist nicht versichert?</h2>
              <ul className="mt-6 space-y-3">
                {service.notCovered.map((n) => (
                  <li key={n} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 mt-0.5">
                      <X className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-muted-foreground leading-relaxed">{n}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <InsuranceDisclaimer category={service.category} />
            </div>
          </div>
        </section>

        {/* FAQ */}
        {faqs.length > 0 && (
          <ServiceFAQ
            id={service.slug}
            title={`Häufige Fragen zu ${service.shortName}`}
            subtitle={`Antworten auf die wichtigsten Fragen rund um ${service.name} – verständlich erklärt.`}
            items={faqs}
          />
        )}

        {/* Internal links (Glossar + Blog) — placed between FAQ and CTA */}
        {hasInternalLinks && (
          <section className="py-16 md:py-20 bg-secondary/40">
            <div className="container max-w-5xl">
              <div
                className={`grid gap-6 ${
                  glossaryItems.length > 0 && blogItems.length > 0 ? "md:grid-cols-2" : "md:grid-cols-1"
                }`}
              >
                {glossaryItems.length > 0 && (
                  <div className="reveal rounded-2xl bg-card border border-border p-6 md:p-8 shadow-card">
                    <div className="flex items-center gap-2 text-accent">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Fachbegriffe</span>
                    </div>
                    <h2 className="mt-2 font-display text-xl md:text-2xl font-bold text-primary">
                      Passende Begriffe im Glossar
                    </h2>
                    <ul className="mt-5 space-y-2.5">
                      {glossaryItems.map((g) => (
                        <li key={g.slug}>
                          <Link
                            to={`/glossar/${g.slug}`}
                            className="group inline-flex items-start gap-2 text-primary hover:text-accent transition-colors"
                          >
                            <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-accent transition-transform group-hover:translate-x-0.5" />
                            <span className="font-medium">{g.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {blogItems.length > 0 && (
                  <div className="reveal rounded-2xl bg-card border border-border p-6 md:p-8 shadow-card">
                    <div className="flex items-center gap-2 text-accent">
                      <BookOpen className="w-4 h-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Ratgeber</span>
                    </div>
                    <h2 className="mt-2 font-display text-xl md:text-2xl font-bold text-primary">
                      Weiterlesen im Blog
                    </h2>
                    <ul className="mt-5 space-y-3">
                      {blogItems.map((b) => (
                        <li key={b.slug}>
                          <Link
                            to={`/blog/${b.slug}`}
                            className="group flex items-start gap-2 text-primary hover:text-accent transition-colors"
                          >
                            <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-accent transition-transform group-hover:translate-x-0.5" />
                            <span className="font-medium leading-snug">{b.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Conclusion / CTA */}
        <section className="py-16 md:py-24 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative max-w-3xl text-center">
            <h2 className="reveal font-display text-3xl md:text-4xl font-bold text-balance">
              Bereit für Deinen passenden Schutz?
            </h2>
            <p className="reveal mt-5 text-lg text-white/80 leading-relaxed">{service.conclusion}</p>
            <div className="reveal mt-8">
              <Button asChild variant="accent" size="xl">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Erstgespräch vereinbaren <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container">
            <h2 className="reveal font-display text-2xl md:text-3xl font-bold text-primary text-center">
              Weitere Leistungen
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((s) => {
                const RIcon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`/leistungen/${s.slug}`}
                    className="reveal group rounded-2xl bg-card border border-border p-6 shadow-card hover:shadow-elegant hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-accent text-accent-foreground flex items-center justify-center shadow-glow">
                      <RIcon className="w-5 h-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold text-primary">{s.shortName}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">{s.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                      Mehr erfahren <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
